import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import {Form, Input, Text} from "tea-component";
import CustomSelect from "@/pages/datalab/components/CustomSelect";

interface Validation {
  status: "error" | "success" | "validating" | undefined;
  message: string;
  messageEn: string;
  required?: boolean;
}

function InfoForm(props: any, ref:any) {
  const { dataFormatMessage, isEn, commonFormatMessage, tableInfo } = props;
  const newInfoRef = useRef({});
  const [validation, setValidation] = useState<{[key:string]: Validation;}>({
    customize_name: {status: undefined, message: '', messageEn: '', required: true},
    table_desc: {status: undefined, message: '', messageEn: '', required: false},
    label: {status: undefined, message: '', messageEn: '', required: false},
    sub_label: {status: undefined, message: '', messageEn: '', required: false},
    cycle: {status: undefined, message: '', messageEn: '', required: false},
  });
  // 一级类目, 二级类目, 表周期的选项数据
  const [optionsMap, setOptionsMap] = useState<Record<string, []>>({});
  useEffect(() => {
    try {
      const old = JSON.parse(sessionStorage.getItem('dbl-table-options') as string || '{}');
      const newOptionsMap: any = {};
      Object.keys(old).forEach((key) => {
        newOptionsMap[key] = old[key].reduce((acc: any[], opt: any) => {
          const text = (isEn ? opt.textEn : opt.text).replace(/\([^()]*\)/g, '');
          (opt.value !== 'all' && opt.value !== '') && (acc.push({...opt, text}));
          return acc;
        }, []);
        const len1 = optionsMap[key]?.length;
        const len2 = newOptionsMap[key]?.length;
				
        // 如果用户自定义了选项, 就需要把自定义选项的值重新放到新的数组中
        if (len1 > len2) {
          newOptionsMap[key]?.push(...optionsMap[key]?.slice(len2));
        }
      });
      setOptionsMap(newOptionsMap);
    } catch (e) {
      console.error("数据表编辑页面的选项中英转换错误", e);
    }
  }, [isEn]);
	
  useImperativeHandle(ref, () => ({
    getNewInfo: () => newInfoRef.current,
    getValidations: () => {
      const validations: any[] = [];
      Object.keys(validation).forEach(key => {
        if (validation[key].status === 'error') {
          validations.push({name: key, isTableInfo: true, ...validation[key]});
        }
      });
      return validations;
    }
  }));
	
	
  // 表名称, 表描述, 一级类目, 二级类目等表的信息
  // const tableInfo = useMemo(() => JSON.parse(sessionStorage.getItem('dbl-table-info') as string || '{}'), []);
  const infoChange = (key: string, value: string) => {
    // 校验
    if (!value && (key !== 'table_desc')) {
      validation[key].message = '必填';
      validation[key].messageEn = 'Required';
      validation[key].status = 'error';
      setValidation({...validation});
    }
    else if ( 'customize_name' === key && value.length > 128) { // 限制长度
      validation[key].message = '最大长度为128字符';
      validation[key].messageEn = 'The maximum length is 128 characters.';
      validation[key].status = 'error';
      setValidation({...validation});
    }
    else {
      newInfoRef.current[key] = value;
      if (validation[key].status === 'error') {
        validation[key].status = undefined;
        validation[key].message = '';
        validation[key].messageEn = '';
        setValidation({...validation});
      }
    }
  };
	
  const selectOpen = (key: string) => {
    if (!tableInfo[key] && !validation[key].required && !newInfoRef.current[key]) {
      validation[key].required = true;
      setValidation({...validation});
    }
  };
  const selectClose = (key: string) => {
    if (!newInfoRef.current[key] && !tableInfo[key]) {
      validation[key].status = 'error';
      validation[key].message = '必填';
      validation[key].messageEn = 'Required';
      setValidation({...validation});
    }
  };
  
  return tableInfo ? (<Form className={'dbl-table-edit-from'}>
    <Form.Item label={dataFormatMessage('origin_name')}>
      <Text style={{fontSize: '14px'}}>{tableInfo.table_name}</Text>
    </Form.Item>
		
    <Form.Item label={dataFormatMessage('name')} showStatusIcon={false} status={validation.customize_name.status} required={true} message={validation.customize_name[isEn ? 'messageEn' : 'message']}>
      <Input
        defaultValue={tableInfo.customize_name || tableInfo.table_name}
        onChange={(value) => {
          infoChange('customize_name', value?.trim() || '');
        }}
      />
    </Form.Item>
		
    <Form.Item label={dataFormatMessage('description')} showStatusIcon={false}>
      <Input.TextArea
        defaultValue={(isEn ? tableInfo.table_desc_en : tableInfo.table_desc) || tableInfo.table_desc}
        onChange={(value) => {
          infoChange("table_desc", value.trim());
        }}
        maxLength={2560}
        showCount={true}
      />
    </Form.Item>
		
    <Form.Item
      label={dataFormatMessage("level_one")}
      showStatusIcon={false}
      status={validation.label.status}
      required={!!validation.label.status}
      message={validation.label[isEn ? 'messageEn' : 'message']}
    >
      <CustomSelect
        placeholder={`${commonFormatMessage('select_placeholder')} ${dataFormatMessage("level_one")}`}
        className={'dbl-table-edit-select'}
        defaultValue={tableInfo.label}
        onChange={(value: string) => {
          infoChange('label', value);
        }}
        options={optionsMap?.label || []}
        customConfirm={(customValue: string) => {
          setOptionsMap((optionsMap: any) => {
            optionsMap?.label?.unshift({
              text: customValue,
              textEn: customValue,
              value: customValue,
            });
            return optionsMap;
          });
        }}
        onOpen={() => selectOpen('label')}
        onClose={() => selectClose('label')}
      />
    </Form.Item>
    <Form.Item
      label={dataFormatMessage("level_two")}
      showStatusIcon={false}
      status={validation.sub_label.status}
      required={!!validation.sub_label.status}
      message={validation.sub_label[isEn ? 'messageEn' : 'message']}
    >
      <CustomSelect
        placeholder={`${commonFormatMessage('select_placeholder')} ${dataFormatMessage("level_two")}`}
        className={'dbl-table-edit-select'}
        defaultValue={tableInfo.sub_label}
        onChange={(value: string) => {
          infoChange('sub_label', value);
        }}
        options={optionsMap?.sub_label || []}
        customConfirm={(customValue: string) => {
          setOptionsMap((optionsMap: any) => {
            optionsMap?.sub_label?.unshift({
              text: customValue,
              textEn: customValue,
              value: customValue,
            });
            return optionsMap;
          });
          // infoChange('sub_label', customValue)
        }}
        onOpen={() => selectOpen('sub_label')}
        onClose={() => selectClose('sub_label')}
      />
    </Form.Item>
    <Form.Item
      label={dataFormatMessage("cycle")}
      showStatusIcon={false}
      status={validation.cycle.status}
      required={!!validation.cycle.status}
      message={validation.cycle[isEn ? 'messageEn' : 'message']}
    >
      <CustomSelect
        placeholder={`${commonFormatMessage('select_placeholder')} ${dataFormatMessage("cycle")}`}
        className={'dbl-table-edit-select'}
        defaultValue={tableInfo.cycle}
        onChange={(value: string) => {
          infoChange('cycle', value);
        }}
        options={optionsMap?.cycle || []}
        customConfirm={(customValue: string) => {
          setOptionsMap((optionsMap: any) => {
            optionsMap?.cycle?.unshift({
              text: customValue,
              textEn: customValue,
              value: customValue,
            });
            return optionsMap;
          });
          // infoChange('cycle', customValue)
        }}
        onOpen={() => selectOpen('cycle')}
        onClose={() => selectClose('cycle')}
      />
    </Form.Item>
	
  </Form>) : '';
}

export default React.forwardRef(InfoForm);